import type { AnalysisReport, AnalysisWarning, RuleAnalysis, RuleReference } from '../../types';

export const REPORT_EXPORT_SCHEMA_VERSION = '1.0.0' as const;
export const REDACTED_ABSOLUTE_INPUT = '[redacted absolute path]' as const;

export interface ReportExportOptions {
  readonly includeProjectName?: boolean;
  readonly projectName?: string | null;
}

export function createExportReport(report: AnalysisReport, options: ReportExportOptions = {}) {
  const projectName = options.includeProjectName
    ? sanitizeProjectName(options.projectName ?? null)
    : null;

  return {
    schemaVersion: REPORT_EXPORT_SCHEMA_VERSION,
    ...(projectName === null ? {} : { project: projectName }),
    generatedAt: report.generatedAt,
    summary: { ...report.summary },
    sources: [...report.sources]
      .sort((left, right) => compareText(left.path, right.path))
      .map((source) => ({
        ...source,
        path: sanitizeRelativePath(source.path),
        directory: sanitizeRelativePath(source.directory),
        ruleIds: [...source.ruleIds],
      })),
    rules: [...report.rules].sort(compareRules).map((rule) => ({
      ...rule,
      source: sanitizeRelativePath(rule.source),
      sourceDirectory: sanitizeRelativePath(rule.sourceDirectory),
      statuses: [...rule.statuses],
      affectedPaths: rule.affectedPaths.map(sanitizeRelativePath).sort(compareText),
    })),
    paths: [...report.paths]
      .sort((left, right) => compareText(left.path, right.path))
      .map((path) => ({
        ...path,
        path: sanitizeRelativePath(path.path),
        decisiveRule: sanitizeRuleReference(path.decisiveRule),
        matchedRules: path.matchedRules.map((match) => ({
          ...match,
          source: sanitizeRelativePath(match.source),
        })),
        ruleSource: path.ruleSource === null ? null : sanitizeRelativePath(path.ruleSource),
        warnings: path.warnings.map(sanitizeWarning).sort(compareWarnings),
      })),
    warnings: report.warnings.map(sanitizeWarning).sort(compareWarnings),
    inputIssues: report.inputIssues.map((issue) => ({
      code: issue.code,
      input: sanitizeRejectedInput(issue.input),
      message: issue.message,
    })),
  };
}

export type JsonReportExportV1 = ReturnType<typeof createExportReport>;

export function exportJson(report: AnalysisReport, options: ReportExportOptions = {}): string {
  return `${JSON.stringify(createExportReport(report, options), null, 2)}\n`;
}

export function exportMarkdown(report: AnalysisReport, options: ReportExportOptions = {}): string {
  const exported = createExportReport(report, options);
  const blocked = exported.warnings.filter((warning) => warning.code === 'blocked-negation');
  const unmatched = exported.rules.filter((rule) => rule.statuses.includes('Unmatched'));
  const broad = exported.rules.filter((rule) => rule.statuses.includes('Broad rule'));
  const lines: string[] = ['# WhatTheIgnore report', ''];

  if (exported.project !== undefined) {
    lines.push(`Project: ${inlineCode(exported.project)}`, '');
  }

  lines.push(
    `Scanned: ${exported.generatedAt}`,
    '',
    '## Summary',
    '',
    `- Paths: ${String(exported.summary.pathCount)}`,
    `- Ignored: ${String(exported.summary.ignoredCount)}`,
    `- Tracked: ${String(exported.summary.trackedCount)}`,
    `- Warnings: ${String(exported.summary.warningCount)}`,
    `- Ignore sources: ${String(exported.summary.sourceCount)}`,
    `- Rules: ${String(exported.summary.ruleCount)}`,
    '',
    '## Blocked negations',
    '',
  );
  appendWarnings(lines, blocked);

  lines.push('', '## Unmatched rules', '');
  appendRules(lines, unmatched);

  lines.push('', '## Broad rules', '');
  appendRules(lines, broad);

  lines.push('', '## All warnings', '');
  appendWarnings(lines, exported.warnings);

  lines.push('', '## Rejected input', '');
  if (exported.inputIssues.length === 0) {
    lines.push('_None._');
  } else {
    for (const issue of exported.inputIssues) {
      lines.push(
        `- **${escapeMarkdown(issue.code)}**: ${escapeMarkdown(issue.message)} (input: ${inlineCode(issue.input)})`,
      );
    }
  }

  lines.push(
    '',
    '_Generated locally by WhatTheIgnore. No non-.gitignore file contents were read._',
    '',
  );
  return lines.join('\n');
}

export function sanitizeRejectedInput(input: string): string {
  return isAbsoluteLike(input) ? REDACTED_ABSOLUTE_INPUT : cleanControlCharacters(input);
}

function sanitizeRuleReference(rule: RuleReference | null): RuleReference | null {
  if (rule === null) {
    return null;
  }
  return { ...rule, source: sanitizeRelativePath(rule.source) };
}

function sanitizeWarning(warning: AnalysisWarning): AnalysisWarning {
  return {
    ...warning,
    paths: warning.paths.map(sanitizeRelativePath).sort(compareText),
    source: warning.source === null ? null : sanitizeRelativePath(warning.source),
    suggestedSource:
      warning.suggestedSource === null ? null : sanitizeRelativePath(warning.suggestedSource),
  };
}

function sanitizeRelativePath(path: string): string {
  if (path.length === 0) {
    return '';
  }
  return isAbsoluteLike(path) ? REDACTED_ABSOLUTE_INPUT : cleanControlCharacters(path);
}

function sanitizeProjectName(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  const segments = value.replaceAll('\\', '/').split('/').filter(Boolean);
  const finalSegment = segments.at(-1);
  if (finalSegment === undefined) {
    return null;
  }
  const clean = cleanControlCharacters(finalSegment).trim();
  return clean.length === 0 ? null : clean;
}

function appendWarnings(lines: string[], warnings: readonly AnalysisWarning[]): void {
  if (warnings.length === 0) {
    lines.push('_None in the current input._');
    return;
  }

  for (const warning of warnings) {
    const location = formatLocation(warning.source, warning.line);
    const affected =
      warning.paths.length === 0 ? 'no current paths' : warning.paths.map(inlineCode).join(', ');
    lines.push(
      `- **${escapeMarkdown(warning.code)}** at ${location}: ${escapeMarkdown(warning.reason)} Affected: ${affected}.`,
    );
  }
}

function appendRules(lines: string[], rules: readonly RuleAnalysis[]): void {
  if (rules.length === 0) {
    lines.push('_None in the current input._');
    return;
  }

  for (const rule of rules) {
    lines.push(
      `- ${formatLocation(rule.source, rule.line)}: matched ${String(rule.matchCount)}, decisive for ${String(rule.decisiveCount)}.`,
    );
  }
}

function formatLocation(source: string | null, line: number | null): string {
  const safeSource = source ?? 'project';
  return line === null ? inlineCode(safeSource) : `${inlineCode(safeSource)} line ${String(line)}`;
}

function inlineCode(value: string): string {
  return `\`${cleanControlCharacters(value).replaceAll('`', '\\`')}\``;
}

function escapeMarkdown(value: string): string {
  return cleanControlCharacters(value).replace(/[\\*_{}[\]()#+.!|>-]/gu, '\\$&');
}

function cleanControlCharacters(value: string): string {
  let result = '';
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    result += code < 32 || code === 127 ? ' ' : value.charAt(index);
  }
  return result;
}

function isAbsoluteLike(value: string): boolean {
  return /^(?:[A-Za-z]:[\\/]|[\\/]{1,2}|file:)/iu.test(value.trimStart());
}

function compareRules(left: RuleAnalysis, right: RuleAnalysis): number {
  return (
    compareText(left.source, right.source) ||
    left.line - right.line ||
    compareText(left.id, right.id)
  );
}

function compareWarnings(left: AnalysisWarning, right: AnalysisWarning): number {
  return compareText(left.id, right.id);
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}
